import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Check, Home, RotateCcw, Trees } from "lucide-react";
import { QUIZ } from "../data/site";
import { saveScope } from "../lib/api";
import { CalendlyEmbed, TrustMicrocopy } from "./Calendly";

const ICONS = { interior: Home, exterior: Trees, commercial: Building2 };
const STEP_TITLES = ["What are we painting?", "Which spaces?", "Current condition?", "When do you need it?"];
const tierFor = (c) => (c === "good" ? "Refresh" : c === "repair" ? "Revive" : "Renew");

const OptionCard = ({ active, onClick, label, sub, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`group flex w-full items-center gap-3 rounded-md border p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-flame bg-flame/10" : "border-slate-200 bg-white hover:border-navy/40"}`}>
    {children}
    <span className="flex-1">
      <span className="block font-display text-base font-bold text-navy">{label}</span>
      {sub && <span className="block text-xs text-slate-600">{sub}</span>}
    </span>
    <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${active ? "border-flame bg-flame text-navy" : "border-slate-300"}`}>
      {active && <Check className="h-3 w-3" />}
    </span>
  </button>
);

const Progress = ({ step }) => (
  <div className="flex gap-1.5" aria-label={`Step ${Math.min(step + 1, 4)} of 4`}>
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200">
        <motion.span className="block h-full bg-flame" initial={false} animate={{ width: step > i ? "100%" : step === i ? "35%" : "0%" }} transition={{ duration: 0.5 }} />
      </span>
    ))}
  </div>
);

const TypeStep = ({ value, pick }) => (
  <div className="grid gap-3" data-testid="hero-scope-quiz-step-1">
    {QUIZ.types.map((t) => {
      const Icon = ICONS[t.id];
      return (
        <OptionCard key={t.id} active={value === t.id} onClick={() => pick(t.id)} label={t.label} sub={t.sub} testId={`quiz-type-${t.id}`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-navy text-white transition-colors group-hover:bg-blue"><Icon className="h-5 w-5" /></span>
        </OptionCard>
      );
    })}
  </div>
);

const SpacesStep = ({ type, value, toggle, next }) => (
  <div data-testid="hero-scope-quiz-step-2">
    <div className="flex flex-wrap gap-2">
      {(QUIZ.spaces[type] || []).map((s) => {
        const on = value.includes(s);
        return (
          <button key={s} type="button" onClick={() => toggle(s)} data-testid={`quiz-space-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-[background-color,border-color,color] ${on ? "border-navy bg-navy text-white" : "border-slate-300 bg-white text-navy hover:border-navy"}`}>
            {s}
          </button>
        );
      })}
    </div>
    <button type="button" disabled={!value.length} onClick={next} className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-40" data-testid="quiz-spaces-continue-btn">
      Continue <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

const ListStep = ({ items, value, pick, testId, prefix }) => (
  <div className="grid gap-2.5 sm:grid-cols-2" data-testid={testId}>
    {items.map((c) => (
      <OptionCard key={c.id} active={value === c.id} onClick={() => pick(c.id)} label={c.label} sub={c.sub} testId={`${prefix}-${c.id}`} />
    ))}
  </div>
);

const Result = ({ a, reset }) => {
  const label = (list, id) => list.find((x) => x.id === id)?.label ?? "";
  const summary = useMemo(() => ({
    Project: label(QUIZ.types, a.type),
    Spaces: a.spaces.join(", "),
    Condition: label(QUIZ.conditions, a.condition),
    Timeline: label(QUIZ.timelines, a.timeline),
  }), [a]);
  const scopeText = Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(" | ");
  const onScheduled = useCallback(() => saveScope({ ...summary, booked: "yes" }), [summary]);
  return (
    <div data-testid="hero-scope-quiz-result">
      <p className="eyebrow text-flame-600">Your assessment scope</p>
      <h3 className="mt-2 text-2xl font-extrabold leading-tight text-navy">This isn't a quote — it's your assessment scope.</h3>
      <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200">
        {Object.entries(summary).map(([k, v]) => (
          <div key={k} className="bg-white p-3">
            <dt className="font-mono text-[10px] uppercase tracking-widest text-slate-600">{k}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-navy" data-testid={`quiz-result-${k.toLowerCase()}`}>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-slate-700">Likely finish tier: <span className="font-bold text-navy" data-testid="quiz-result-tier">{tierFor(a.condition)}</span>. Ilias confirms everything in person.</p>
      <div className="mt-5"><CalendlyEmbed scopeText={scopeText} height={520} onScheduled={onScheduled} testId="hero-calendly-embed" /></div>
      <div className="mt-3"><TrustMicrocopy /></div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Link to="/book" onClick={() => saveScope(summary)} className="btn-primary" data-testid="quiz-result-book-btn">Prefer a form? Request a time <ArrowRight className="h-4 w-4" /></Link>
        <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-navy" data-testid="quiz-reset-btn"><RotateCcw className="h-4 w-4" /> Start over</button>
      </div>
    </div>
  );
};

const EMPTY = { type: "", spaces: [], condition: "", timeline: "" };

export const ScopeQuiz = () => {
  const [step, setStep] = useState(0);
  const [a, setA] = useState(EMPTY);
  const go = (n) => setStep(n);
  const set = (k, v, nextStep) => { setA((s) => ({ ...s, [k]: v, ...(k === "type" && s.type !== v ? { spaces: [] } : {}) })); if (nextStep !== undefined) setTimeout(() => go(nextStep), 220); };
  const toggle = (s) => setA((x) => ({ ...x, spaces: x.spaces.includes(s) ? x.spaces.filter((y) => y !== s) : [...x.spaces, s] }));

  const body = [
    <TypeStep key="t" value={a.type} pick={(v) => set("type", v, 1)} />,
    <SpacesStep key="s" type={a.type} value={a.spaces} toggle={toggle} next={() => go(2)} />,
    <ListStep key="c" items={QUIZ.conditions} value={a.condition} pick={(v) => set("condition", v, 3)} testId="hero-scope-quiz-step-3" prefix="quiz-condition" />,
    <ListStep key="l" items={QUIZ.timelines} value={a.timeline} pick={(v) => set("timeline", v, 4)} testId="hero-scope-quiz-step-4" prefix="quiz-timeline" />,
    <Result key="r" a={a} reset={() => { setA(EMPTY); go(0); }} />,
  ];

  return (
    <div className="rounded-lg bg-paper p-5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] sm:p-7" data-testid="hero-scope-quiz">
      <div className="mb-5 flex items-center justify-between gap-4">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600">Scope builder · {step < 4 ? `Step ${step + 1} of 4` : "Done"}</p>
        {step > 0 && step < 4 && (
          <button type="button" onClick={() => go(step - 1)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-navy" data-testid="quiz-back-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        )}
      </div>
      <Progress step={step} />
      {step < 4 && <h2 className="mb-5 mt-6 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-[1.7rem]">{STEP_TITLES[step]}</h2>}
      <div className={step === 4 ? "mt-6" : ""}>
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          {body[step]}
        </motion.div>
      </div>
    </div>
  );
};
